/**
 * Consent screen (01) — per PRD V1 §5.1.
 *
 * First-launch only. The user MUST acknowledge before the app proceeds. Sets
 * the consent flag in localStorage so the modal does not re-appear.
 */

import { useNavigate } from 'react-router-dom';
import { persistence } from '@/services/persistence';
import { whisperAdapter } from '@/services/whisperAdapter';
import { cn } from '@/lib/utils';

export function ConsentScreen() {
  const navigate = useNavigate();

  const handleAcknowledge = () => {
    persistence.setConsentFlag(true);
    // Kick off the Whisper model fetch in the background while the user
    // configures their session. Best-effort: any failure is swallowed
    // here and surfaced later (SessionConfig submit retries / waits, or
    // transcription degrades gracefully per PRD §10.2).
    void whisperAdapter.ensureLoaded().catch(() => {
      /* logged inside the adapter; nothing else to do here */
    });
    // Bounce through `/` so the EntryFlowDecider routes the user to the right
    // next step (Resume Prompt if a session is somehow already stored, else
    // Session Config).
    navigate('/', { replace: true });
  };

  return (
    <main className="flex min-h-dvh items-center justify-center bg-surface-app p-4 sm:p-8">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-elevated p-6 shadow-lg sm:p-8">
        <h1 className="mb-4 text-2xl font-bold tracking-tight text-text-primary">
          Before you play
        </h1>

        <p className="mb-6 text-sm leading-relaxed text-text-secondary">
          PokerSharp records your voice during decisions to transcribe and grade
          what you said. Transcripts go to an LLM for grading. Your session is
          stored on your device until the session ends —{' '}
          <span className="text-text-primary">
            nothing is kept on our servers, ever.
          </span>
        </p>

        <button
          type="button"
          onClick={handleAcknowledge}
          className={cn(
            'w-full rounded-md bg-brand-primary px-4 py-3 text-base font-semibold',
            'text-text-on-brand transition-colors',
            'hover:bg-brand-primary-hover active:bg-brand-primary-active',
            'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-2',
          )}
        >
          Got it — let's play.
        </button>
      </div>
    </main>
  );
}
